/**
 * @file inward-ocr.ts
 * @module app/modules/inward/utils/inward-ocr
 * @description Reads Invoice number, Receipt number and Receipt verification code from
 *   photos of invoices / TRA EFD receipts on the device (nothing is sent to another service):
 *   - the receipt QR code (verify.tra.go.tz/<CODE>_<time>) gives the verification code;
 *   - text recognition (tesseract.js) gives the labelled numbers.
 *   PDFs and other non-image files are skipped (Finance types those values).
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

export interface OcrFields {
  invoiceNumber?: string;
  receiptNumber?: string;
  receiptVerificationCode?: string;
}

export type OcrFieldKey = keyof OcrFields;

const VALUE = /^[:#.\-\s]*([A-Z0-9][A-Z0-9\-/]{1,39})/i;

/** Words that follow a label but are not values (e.g. "RECEIPT NO DATE"). */
const NOT_VALUES = new Set([
  "DATE",
  "TIME",
  "NO",
  "NUMBER",
  "CODE",
  "TOTAL",
  "AMOUNT",
  "TIN",
  "VRN",
]);

const LABELS: Record<OcrFieldKey, RegExp[]> = {
  receiptVerificationCode: [
    /RECEIPT\s*VERIFICATION\s*CODE/i,
    /VERIFICATION\s*CODE/i,
    /\bRCT\s*V\.?\s*CODE/i,
    /\bV\.?\s*CODE\b/i,
  ],
  receiptNumber: [
    /RECEIPT\s*(?:NO|NUMBER|NUM|#)\.?/i,
    /\bRCT\s*(?:NO|NUMBER|#)\.?/i,
  ],
  invoiceNumber: [
    /(?:TAX\s*)?INVOICE\s*(?:NO|NUMBER|NUM|#)\.?/i,
    /\bINV\s*(?:NO|NUMBER|#)\.?/i,
    /\bBILL\s*(?:NO|NUMBER)\.?/i,
  ],
};

const cleanValue = (raw: string) => raw.replace(/[.,;]+$/, "").toUpperCase();

/** Value right after a label on the same line, or on the next non-empty line. */
const valueAfter = (
  lines: string[],
  index: number,
  label: RegExp
): string | undefined => {
  const line = lines[index];
  const m = label.exec(line);
  if (!m) return undefined;
  const rest = line.slice(m.index + m[0].length);
  const same = VALUE.exec(rest);
  if (same && !NOT_VALUES.has(same[1].toUpperCase()))
    return cleanValue(same[1]);
  if (rest.trim() === "" || /^[:#\s]*$/.test(rest)) {
    for (let i = index + 1; i < Math.min(lines.length, index + 3); i += 1) {
      const next = lines[i].trim();
      if (!next) continue;
      const v = VALUE.exec(next);
      if (v && !NOT_VALUES.has(v[1].toUpperCase()) && v[1].length >= 3)
        return cleanValue(v[1]);
      break;
    }
  }
  return undefined;
};

/** Finds the three values in recognised text. */
export const parseOcrText = (text: string): OcrFields => {
  const lines = (text ?? "")
    .split(/\r?\n/)
    .map((l) => l.replace(/[|]/g, " ").replace(/\s+/g, " ").trim());
  const found: OcrFields = {};
  (Object.keys(LABELS) as OcrFieldKey[]).forEach((key) => {
    for (const label of LABELS[key]) {
      for (let i = 0; i < lines.length && !found[key]; i += 1) {
        // "RECEIPT VERIFICATION CODE" must not be read as a receipt number label
        if (key === "receiptNumber" && /VERIFICATION/i.test(lines[i])) continue;
        const v = valueAfter(lines, i, label);
        if (v) found[key] = v;
      }
      if (found[key]) break;
    }
  });
  return found;
};

/** Verification code from a TRA EFD receipt QR (https://verify.tra.go.tz/<CODE>_<HHMMSS>). */
export const parseReceiptQr = (data: string | null | undefined): OcrFields => {
  if (!data) return {};
  const tra = /verify\.tra\.go\.tz\/(?:verify\/)?([A-Z0-9]+)(?:_\d+)?/i.exec(
    data
  );
  if (tra) return { receiptVerificationCode: tra[1].toUpperCase() };
  const param = /(?:rctvcode|vcode|verificationcode)=([A-Z0-9]+)/i.exec(data);
  return param ? { receiptVerificationCode: param[1].toUpperCase() } : {};
};

/** First value wins (earlier documents / QR before text). */
export const mergeOcrFields = (...all: OcrFields[]): OcrFields =>
  all.reduce<OcrFields>((acc, f) => {
    (Object.keys(f) as OcrFieldKey[]).forEach((k) => {
      if (!acc[k] && f[k]) acc[k] = f[k];
    });
    return acc;
  }, {});

export const isReadableImage = (file: File): boolean =>
  /^image\//i.test(file.type) || /\.(jpe?g|png|webp|bmp|gif)$/i.test(file.name);

// ---------------------------------------------------------------------------
// Engine (browser only; loaded on first use)
// ---------------------------------------------------------------------------

type TesseractWorker = {
  recognize: (image: unknown) => Promise<{ data: { text: string } }>;
  terminate: () => Promise<unknown>;
};

let workerPromise: Promise<TesseractWorker> | null = null;

const getWorker = (): Promise<TesseractWorker> => {
  if (!workerPromise) {
    workerPromise = import("tesseract.js")
      .then(
        (m) =>
          (m.default ?? m).createWorker(
            "eng"
          ) as unknown as Promise<TesseractWorker>
      )
      .catch((err) => {
        workerPromise = null;
        throw err;
      });
  }
  return workerPromise;
};

/** Ends the text reader (frees memory when the Finance screen closes). */
export const stopOcr = async () => {
  const pending = workerPromise;
  workerPromise = null;
  if (pending) {
    try {
      await (await pending).terminate();
    } catch {
      /* already stopped */
    }
  }
};

const loadImage = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("unreadable image"));
    };
    img.src = url;
  });

/** QR code text from an image, or null. */
const readQr = async (file: File): Promise<string | null> => {
  const img = await loadImage(file);
  // Large photos are scaled down: faster and QR detection is still reliable
  const scale = Math.min(
    1,
    1600 / Math.max(img.naturalWidth || 1, img.naturalHeight || 1)
  );
  const width = Math.max(1, Math.round((img.naturalWidth || 1) * scale));
  const height = Math.max(1, Math.round((img.naturalHeight || 1) * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0, width, height);
  const { data } = ctx.getImageData(0, 0, width, height);
  const jsQR = (await import("jsqr")).default;
  return jsQR(data, width, height)?.data ?? null;
};

export interface OcrResult {
  fields: OcrFields;
  /** files that could not be read (not images, or reading failed) */
  skipped: string[];
}

/**
 * Reads the given documents one after another (photos only). `onProgress` gets the file name
 * being read. Never throws: unreadable files are listed in `skipped`.
 */
export const readDocuments = async (
  files: File[],
  onProgress?: (fileName: string) => void
): Promise<OcrResult> => {
  const results: OcrFields[] = [];
  const skipped: string[] = [];
  for (const file of files) {
    if (!isReadableImage(file)) {
      skipped.push(file.name);
      continue;
    }
    onProgress?.(file.name);
    let qr: OcrFields = {};
    try {
      qr = parseReceiptQr(await readQr(file));
    } catch {
      /* no QR or unreadable: text recognition may still work */
    }
    try {
      const worker = await getWorker();
      const { data } = await worker.recognize(file);
      results.push(mergeOcrFields(qr, parseOcrText(data.text)));
    } catch {
      if (Object.keys(qr).length) results.push(qr);
      else skipped.push(file.name);
    }
  }
  return { fields: mergeOcrFields(...results), skipped };
};
